
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Footer from '../../../components/feature/Footer';
import TableOfContents, { generateTocItems } from '../../../components/feature/TableOfContents';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const tocItems = generateTocItems([
  'Overview of the El Paso Home Selling Timeline',
  'Stage 1 Pre-Listing Preparation 2 to 4 Weeks',
  'Stage 2 Active Listing and Showings 3 to 8 Weeks',
  'Stage 3 Offer and Negotiation 3 to 10 Days',
  'Stage 4 Under Contract Option and Due Diligence 30 to 45 Days',
  'Stage 5 Closing Day 1 to 3 Days',
  'Common Delays That Extend the El Paso Selling Timeline',
  'How to Sell Your El Paso Home Faster',
  'The Cash Sale Timeline 7 to 14 Days',
  'Frequently Asked Questions About the El Paso Selling Timeline',
  'Plan Your Sale and Take Control of Your Timeline',
]);

export default function ElPasoHomeSellingTimelinePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/c0e6113e-28a2-41ed-b2dc-22e3f9d83ef9.png"
              alt="Cash For Houses El Paso"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/get-cash"
              className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer"
            >
              Blog
            </Link>
            <a
              href="tel:9157457883"
              className="px-6 py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
            >
              Call: 915-745-7883
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <Link
              to="/get-cash"
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-black transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
          </div>

          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full">
              SELLING TIMELINE
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight">
            El Paso Home Selling Timeline: What to Expect at Every Stage
          </h1>

          <div className="flex items-center gap-6 text-sm text-gray-600 mb-10">
            <div className="flex items-center gap-2">
              <i className="ri-user-line"></i>
              <span>Cash For Houses El Paso</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              <span>26 min read</span>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-xl mb-12 h-96">
            <img
              src="https://public.readdy.ai/ai/img_res/7248730b7674079b3c1e68ce1d38c897.jpg"
              alt="El Paso Texas residential neighborhood showing homes at various stages of the selling process"
              title="El Paso home selling timeline - what to expect at every stage when selling your house"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16" itemScope itemType="https://schema.org/Article">
        <meta
          itemProp="headline"
          content="El Paso Home Selling Timeline: What to Expect at Every Stage"
        />
        <meta itemProp="author" content="Cash For Houses El Paso" />
        <div className="max-w-3xl mx-auto px-6">
          <TableOfContents items={tocItems} />
          <ArticlePartOne />
          <ArticlePartTwo />
          <ArticlePartThree />
          <ArticlePartFour />
        </div>
      </article>

      {/* Related Posts */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-black mb-10">Related Articles</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link
              to="/get-cash/el-paso-closing-costs-seller-fees"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=real%20estate%20closing%20documents%20and%20settlement%20statement%20spread%20on%20polished%20wooden%20conference%20table%20with%20pen%20and%20house%20keys%2C%20professional%20title%20company%20office%20setting%20with%20warm%20natural%20lighting%2C%20El%20Paso%20Texas%20home%20sale%20closing%20process%20with%20financial%20paperwork%20showing%20fees%20and%20costs%20breakdown%2C%20clean%20documentary%20photography%20of%20seller%20reviewing%20closing%20disclosure&width=600&height=400&seq=closing-costs-ep-hero-main-001&orientation=landscape"
                  alt="El Paso closing costs and seller fees"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                  CLOSING COSTS
                </span>
                <h3 className="text-xl font-semibold text-black mb-2">
                  El Paso Closing Costs &amp; Seller Fees Guide
                </h3>
                <p className="text-sm text-gray-600">
                  Every fee you will pay at closing, with real dollar amounts and strategies to save.
                </p>
              </div>
            </Link>

            <Link
              to="/get-cash/sell-house-fast-el-paso"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20transaction%20with%20sold%20sign%20in%20front%20of%20modern%20El%20Paso%20home%2C%20bright%20sunny%20day%20with%20clear%20blue%20sky%2C%20successful%20home%20sale%20celebration%20with%20keys%20and%20documents%2C%20warm%20welcoming%20atmosphere%20photography&width=600&height=400&seq=blog-list-001&orientation=landscape"
                  alt="Sell house fast in El Paso"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                  SELLING TIPS
                </span>
                <h3 className="text-xl font-semibold text-black mb-2">
                  How to Sell Your House Fast in El Paso
                </h3>
                <p className="text-sm text-gray-600">
                  A complete guide to the fastest methods for selling your El Paso home.
                </p>
              </div>
            </Link>

            <Link
              to="/market-data-el-paso-texas"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20El%20Paso%20Texas%20housing%20market%20with%20residential%20neighborhoods%20stretching%20toward%20Franklin%20Mountains%20at%20golden%20hour%2C%20warm%20desert%20sunset%20colors%20illuminating%20diverse%20communities%20and%20real%20estate%20developments%2C%20professional%20drone%20photography%20showing%20the%20scale%20of%20the%20border%20city%20housing%20landscape%20with%20mountain%20silhouettes%20and%20clear%20sky&width=600&height=400&seq=market-data-ep-hero-001&orientation=landscape"
                  alt="El Paso housing market data"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                  MARKET DATA
                </span>
                <h3 className="text-xl font-semibold text-black mb-2">
                  El Paso Housing Market Data &amp; Trends (2025)
                </h3>
                <p className="text-sm text-gray-600">
                  Comprehensive overview of home values, pricing, and market forecasts.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2C2C2C] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Skip the Months‑Long Timeline. Get a Cash Offer Today.
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Sell your El Paso home in as little as 7 days—no repairs, no showings, no waiting
          </p>

          <a
            href="tel:9157457883"
            className="inline-block text-5xl font-bold text-white mb-8 hover:text-[#D4AF37] transition-colors cursor-pointer"
          >
            915-745-7883
          </a>

          <div>
            <Link
              to="/#contact-form"
              className="inline-block px-12 py-4 bg-white text-black text-lg font-semibold rounded-full hover:scale-105 hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer"
            >
              Get Your Cash Offer
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
